import { useEffect, useState } from "react";
import { FileArrowDown, FilePdf, FileXls } from "@phosphor-icons/react";
import api, { API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

export default function Reports() {
  const [routes, setRoutes] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [months, setMonths] = useState(6);

  useEffect(() => {
    api.get("/routes").then((r) => setRoutes(r.data));
  }, []);

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const toggleAll = () => {
    if (selected.size === routes.length) setSelected(new Set());
    else setSelected(new Set(routes.map((r) => r.id)));
  };

  const download = async (kind) => {
    if (selected.size === 0) {
      toast.error("Select at least one route");
      return;
    }
    const ids = Array.from(selected).join(",");
    const url = `${API}/reports/${kind}?route_ids=${encodeURIComponent(ids)}&months=${months}`;
    try {
      const r = await fetch(url, { credentials: "include",
        headers: { Authorization: `Bearer ${localStorage.getItem("saa_token") || ""}` }});
      if (!r.ok) throw new Error("failed");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `saa_fares.${kind === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      toast.success("Download started");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1400px]">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em]" style={{ color: "var(--saa-gold)" }}>
          <FileArrowDown size={14} weight="duotone" /> Export
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2" data-testid="reports-title">Reports</h1>
        <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>
          Generate consolidated PDF or Excel reports for one or many routes.
        </p>
      </div>

      <div className="saa-card p-5 mb-6">
        <div className="flex flex-wrap items-end gap-4 justify-between">
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--text-muted)" }}>
              Window
            </label>
            <Select value={`${months}`} onValueChange={(v) => setMonths(parseInt(v))}>
              <SelectTrigger className="w-[140px]" data-testid="reports-months"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3 months</SelectItem>
                <SelectItem value="6">6 months</SelectItem>
                <SelectItem value="9">9 months</SelectItem>
                <SelectItem value="12">12 months</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => download("excel")} data-testid="export-excel">
              <FileXls size={16} weight="duotone" className="mr-1" /> Export Excel
            </Button>
            <Button onClick={() => download("pdf")} style={{ background: "var(--saa-gold)", color: "#0A1428" }} data-testid="export-pdf">
              <FilePdf size={16} weight="duotone" className="mr-1" /> Export PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="saa-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--text-muted)" }}>
            Select routes ({selected.size}/{routes.length})
          </div>
          <button onClick={toggleAll} className="text-xs hover:underline" data-testid="select-all" style={{ color: "var(--saa-gold)" }}>
            {selected.size === routes.length ? "Clear all" : "Select all"}
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {routes.map((r) => (
            <label key={r.id} className="flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-white/5"
                   data-testid={`route-check-${r.id}`}>
              <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggle(r.id)} />
              <span className="font-mono text-sm">{r.origin}-{r.destination}</span>
              <span className="text-xs ml-auto" style={{ color: "var(--text-muted)" }}>
                {r.destination_city}
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
